import { LAYERS } from '@/lib/constants/model'
import type { Model } from '@/lib/three-d-resources/model'
import type { SceneConfig } from '@/lib/three-d-resources/scene-config'
import type { ThreeDRenderModule } from '@/lib/three-d-resources/three-d-render-module'
import { markRaw } from 'vue'
import { gsap } from 'gsap'
import type { Object3D, PerspectiveCamera, Texture, Vector2, Vector3 } from 'three'
import {
  Box3,
  Clock,
  Color,
  ColorManagement,
  CubeReflectionMapping,
  DoubleSide,
  EquirectangularReflectionMapping,
  Euler,
  LinearSRGBColorSpace,
  Mesh,
  Quaternion,
  Raycaster,
  Scene,
  ShaderMaterial,
  Sphere,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import type { Pass } from 'three/examples/jsm/postprocessing/Pass'
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass'
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader'
import type { NodeVisibility } from '~/entities/model3d'
import { getEditControls, initEditControls } from '../three-d-editor/edit-controls'
import { TransparencyPass } from '~/lib/three-d-resources/passes/transparencyPass'
import { disposeScene } from '~/lib/three-d-viewer/utils'
import { getReadOnlyControls, initReadOnlyControls } from '~/lib/three-d-resources/controls/readonly-controls'
import type { ViewerControls } from './controls/viewerControls'
import { DEFAULT_MODEL_TWEEN_DURATION } from './controls/viewerControls'
import type { TextLabel, LabelsManager } from './labels-manager'
import { HIGHLIGHT_COLORS, LayerHighlightManager } from '@/lib/three-d-resources/layer-highlight-manager'
import type { TargetVisualizer } from '@/lib/three-d-resources/target-visualizer'
import { nodeId } from '@/lib/three-d-resources/utils'
import type { DrillDataSetManager } from '@/lib/three-d-resources/drill-data-set-manager'
import {
  fragmentShader as backdropFragmentShader,
  vertexShader as backdropVertexShader,
} from '@/lib/three-d-resources/shaders/thumbnail-backdrop'
import { debounce } from '~/lib/utils'
import { DEBUG } from './debugger'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'

export type HitResult = {
  point: Vector3
  nodeId: number
  object: Object3D
  label?: TextLabel
  drillLabel?: Object3D
  normal?: Vector3
  distance: number
  billboard: boolean
  grabbable: boolean
}

ColorManagement.enabled = false

export class World {
  renderer: WebGLRenderer = new WebGLRenderer({ alpha: true, logarithmicDepthBuffer: true })
  private clock = new Clock()
  private _pluginModules = new Map<symbol, ThreeDRenderModule | Pass>()
  // Watch for resize of the container and resize canvas accordingly
  private _resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
    const { width, height } = entries[0].contentRect
    this.setSize(width, height)
  })
  _resizeContainer: HTMLElement | null = null
  _effectComposer: EffectComposer
  scene = new Scene()
  currentModel: Model | null = null
  currentSceneConfig: SceneConfig | null = null
  labelsManager: LabelsManager | null = null
  targetVisualizer: TargetVisualizer | null = null
  controls: ViewerControls
  layerHighlightManager: LayerHighlightManager | null = null
  private transparencyPass: TransparencyPass
  private transparencyOldScene = new Scene()
  private transparencyOldSceneOpacity = { value: 0 }
  private transparencyTween: gsap.core.Tween | null = null
  private intersectionObserver: IntersectionObserver | null = null
  private pauseRendering = false
  public changedLayers: Record<number, boolean> = {}
  public needsRender = false
  drillDataSetManager: DrillDataSetManager | null = null

  private backdropMaterial = new ShaderMaterial({
    fragmentShader: backdropFragmentShader,
    vertexShader: backdropVertexShader,
    uniforms: {
      aspectRatio: { value: 1 },
      color1: { value: new Color(0x4a4a4a) },
      color2: { value: new Color(0x353535) },
    },
    side: DoubleSide,
  })
  private backdropQuad = new FullScreenQuad(this.backdropMaterial)

  constructor(
    public currentCamera: PerspectiveCamera,
    private onLayerVisibilityChange: () => void,
    accessType: 'edit' | 'view',
  ) {
    const dpr = window.devicePixelRatio
    const { width, height } = this.renderer.domElement
    this.scene.name = 'World'
    this.transparencyOldScene.name = 'OldWorld'

    this.renderer.setPixelRatio(dpr)
    this.renderer.domElement.style.position = 'absolute'
    this.renderer.setClearColor(0xffffff, 0)
    // Must be attached after the renderer is created so it runs after three.js's restored handler
    this.renderer.domElement.addEventListener('webglcontextrestored', this._onContextRestored)
    this.renderer.domElement.inert = true
    this.renderer.outputColorSpace = LinearSRGBColorSpace
    this.renderer.info.autoReset = false

    if (accessType === 'view') {
      initReadOnlyControls(currentCamera /* , ctx.root.$analytics */) // We can add anlyatics back in, if requested
      this.controls = markRaw(getReadOnlyControls())
    } else {
      initEditControls(currentCamera)
      this.controls = markRaw(getEditControls())
    }

    this._effectComposer = new EffectComposer(
      this.renderer,
      new WebGLRenderTarget(this.renderer.domElement.width * dpr, this.renderer.domElement.height * dpr, { samples: 8 }),
    )

    this.transparencyPass = new TransparencyPass(this.transparencyOldScene, this.currentCamera, width, height)
    this.transparencyPass.enabled = false

    const renderPass = new RenderPass(this.scene, this.currentCamera)
    const SwapDisplayBuffers = new ShaderPass(CopyShader)
    this.layerHighlightManager = new LayerHighlightManager(
      {
        color: HIGHLIGHT_COLORS.BLUE,
        opacity: 0.5,
        layerMask: LAYERS.HIGHLIGHT,
      },
      this.scene,
      this.currentCamera,
    )

    this._effectComposer.addPass(renderPass)
    this._effectComposer.addPass(this.layerHighlightManager)
    this._effectComposer.addPass(this.transparencyPass)

    //should always come last
    this._effectComposer.addPass(SwapDisplayBuffers)

    this.updateNearFarPlanes()
  }

  hitTest(normalizedMousePosition: Vector2, { includeLabels = false }: { includeLabels?: boolean } = {}): HitResult | null {
    // Ray cast for now. We can do better later if we need rollover highlighting or something
    const raycaster = new Raycaster()
    raycaster.firstHitOnly = true // optimization used by three-mesh-bvh extension
    raycaster.setFromCamera(normalizedMousePosition, this.currentCamera)

    let objects = this.currentModel?.scene.children ?? []
    if (includeLabels) {
      objects = objects.concat(this.labelsManager?.group.children ?? [])
      objects = objects.concat(this.drillDataSetManager?.drillLabelManager.group.children ?? [])
    }

    const intersects = raycaster.intersectObjects(objects, true)
    const firstVisibleHit = intersects.find(layer => {
      let visibleObject = layer.object.visible

      if (layer.object.userData.opacityStatus?.visible === false) {
        visibleObject = false
      }

      // if object is visible, check parents
      if (visibleObject) {
        layer.object.traverseAncestors(parent => {
          if (!parent.visible || parent.userData.opacityStatus?.visible === false) {
            visibleObject = false
          }
        })
      }

      return visibleObject
    })

    if (!firstVisibleHit) {
      return null
    }

    const { point, object, normal, distance } = firstVisibleHit

    let root: Object3D | null = object
    while (root) {
      const rootId = nodeId(root)!
      if (this.currentModel?.apiLayersById.has(rootId)) break
      if (root.userData.labelId) break
      if (root.userData.drillLabelId) break
      root = root.parent
    }

    let label: TextLabel | undefined
    if (root?.userData.labelId) {
      label = this.labelsManager?.getLabel(root.userData.labelId) ?? undefined
    }
    let drillLabel: Object3D | undefined
    if (root?.userData.drillLabelId) {
      drillLabel = root
    }

    if (!root) {
      return null
    }

    return {
      point,
      nodeId: nodeId(root)!,
      object,
      label,
      drillLabel,
      normal,
      distance,
      billboard: !!this.findBillboard(object),
      grabbable: object.name === 'flagpoleGrabbable',
    }
  }

  // returns the billboard node if one exists in the provided node's traversal
  findBillboard(node: Object3D): Object3D | null {
    for (let billboard of this.currentModel?.billboards ?? []) {
      let parent: Object3D | null = billboard
      while (parent) {
        if (node === parent) return billboard
        parent = parent.parent
      }
    }
    return null
  }

  focusObjectInViewer(object: Object3D) {
    const sphere = new Sphere()
    new Box3().setFromObject(object).getBoundingSphere(sphere)
    const distance = _getObjectFitDistance(this.currentCamera, sphere.radius * 2)
    const position = this.currentCamera.position.clone().sub(sphere.center).normalize().multiplyScalar(distance).add(sphere.center)

    this.controls.cameraControls.setLookAt(...position.toArray(), ...sphere.center.toArray(), true)
  }

  isNodeVisible(nodeId: number): boolean {
    if (!this.currentModel) return false

    const sceneNode = this.currentModel.layerNodesById.get(nodeId)

    if (sceneNode?.userData.isVisibleForLayerHighlighting) {
      return sceneNode.userData.wasVisible ?? false
    }

    return sceneNode?.visible ?? false
  }

  mount(element: HTMLElement): void {
    if (element === this._resizeContainer) {
      return
    }

    // Unmount from the old container
    if (this._resizeContainer) {
      this._resizeObserver.unobserve(this._resizeContainer)
    }

    // Mount to the new container
    this._resizeObserver.observe(element)
    this._resizeContainer = element

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.pauseRendering = !entry.isIntersecting
          if (!this.pauseRendering) this.intersectionObserver?.disconnect()
        })
      },
      {
        threshold: 0.1,
      },
    )

    this.intersectionObserver.observe(element)

    this.controls.attach(element)
    if (this.currentModel?.sceneSize) {
      this.controls.setMaxDistance(this.currentModel.sceneSize)
    }

    element.appendChild(this.renderer.domElement)
    this.drillDataSetManager?.mount(element.querySelector<HTMLElement>('.label-container')!)

    const { width, height } = element.getBoundingClientRect()
    this.setSize(width, height)

    this.resetToSceneConfig()
  }

  private setSize(width: number, height: number) {
    // Ensure size is an integer and at least 1 x 1
    // Non-integer values can cause blurring
    width = Math.floor(Math.max(1, width))
    height = Math.floor(Math.max(1, height))

    this.renderer.setSize(width, height) // Set the renderer size too so the canvas gets resized
    this._effectComposer.setSize(width, height)

    this.currentCamera.aspect = width / height
    this.currentCamera.updateProjectionMatrix()

    this.labelsManager?.setSize(width, height)
    this.drillDataSetManager?.setSize(width, height)
    for (let [_, module] of this._pluginModules) {
      module.setSize?.(width, height)
    }
  }

  private _onContextRestored = () => {
    // For some reason we need to trigger... something... to get it to redraw.
    // This works and so does resizing the window (but can't do that programmatically)
    this.render()
  }

  resetResources() {
    this.labelsManager?.resetData()
    this.transparencyTween?.kill()
    this.cleanUpOldSceneTransition()
    this.controls.detach()
    this.renderer.clear()
    disposeScene(this.scene)
    this.backdropQuad.dispose()
    this.drillDataSetManager?.cleanUp()
    this.currentSceneConfig = null
    this.changedLayers = {}

    this.layerHighlightManager?.dispose()
    if (this.currentModel) {
      this.scene.remove(this.currentModel?.scene)
      this.currentModel = null
    }
  }

  async resetToSceneConfig() {
    if (!this.currentModel?.id) return

    this.labelsManager?.fetchLabelsForModel(this.currentModel.id, this.currentModel.apiLayersById)
    await this.drillDataSetManager?.loadDrillDataSet({ companyId: this.currentModel.apiData.company_id, modelId: this.currentModel.id })

    if (this.currentSceneConfig) {
      this.drillDataSetManager?.setSceneId(this.currentSceneConfig.id)
    }
    this.changedLayers = {}
  }

  setModel(model: Model) {
    if (this.currentModel) {
      disposeScene(this.currentModel.scene)
      this.scene.remove(this.currentModel.scene)
    }
    this.currentModel = model
    this.layerHighlightManager!.currentModel = model
    this.scene.add(model.scene)
    this.needsRender = true
  }

  setSceneConfig(sceneConfig: SceneConfig) {
    if (sceneConfig.model.id !== this.currentModel?.id) return
    this.currentSceneConfig = sceneConfig
    this.changedLayers = {}
  }

  setLabelsManager(labelsManager: LabelsManager) {
    if (this.labelsManager) {
      disposeScene(this.labelsManager.group)
      this.scene.remove(this.labelsManager.group)
    }

    this.labelsManager = labelsManager
    this.scene.add(this.labelsManager.group)
  }

  setDrillDataSetManager(drillDataSetManager: DrillDataSetManager) {
    this.drillDataSetManager = drillDataSetManager

    this.drillDataSetManager.cleanUp()

    if (this._resizeContainer) {
      this.drillDataSetManager.mount(this._resizeContainer.querySelector<HTMLElement>('.label-container')!)
    }
    this.scene.add(this.drillDataSetManager.drillLabelManager.group)
    this.scene.add(this.drillDataSetManager.drillHoleManager.group)
    this.scene.add(this.drillDataSetManager.doraClusterManager.group)
  }

  setTargetVisualizer(targetVisualizer: TargetVisualizer) {
    if (this.targetVisualizer) {
      this.targetVisualizer.resetData()
    }

    this.targetVisualizer = targetVisualizer
  }

  updateNearFarPlanes() {
    this.currentCamera.near = 1
    this.currentCamera.far = 1e9
  }

  debugRender() {
    if (!DEBUG.get().isDebug()) return
    const info = this.renderer.info

    DEBUG.get().set('geo', info.memory.geometries)
    DEBUG.get().set('tex', info.memory.textures)
    DEBUG.get().set('mats', info.programs?.length)
    DEBUG.get().set('mats', info.programs?.length)
    DEBUG.get().set('tris', info.render.triangles)
    DEBUG.get().set('calls', info.render.calls)

    this.currentModel?.layerNodesById.forEach(layer => {
      if (layer.visible) {
        layer.traverse(object => {
          if (object instanceof Mesh) {
            DEBUG.get().totalObjects[object.uuid] = object
          }
        })
      }
    })
  }

  render() {
    DEBUG.get().start('r')
    const frames = DEBUG.get().frame('fps')

    if (this.pauseRendering) return

    const delta = this.clock.getDelta()

    DEBUG.get().start('billboard_time')
    this.currentModel?.billboards.forEach(billboard => {
      const tempQuaternion = new Quaternion()
      if (billboard.parent) {
        tempQuaternion.setFromRotationMatrix(billboard.parent?.matrixWorld).invert()
      } else {
        tempQuaternion.set(0, 0, 0, 1) // Identity
      }
      tempQuaternion.multiply(this.currentCamera.quaternion)
      billboard.quaternion.copy(tempQuaternion)
    })
    DEBUG.get().end('billboard_time')

    this.transparencyPass.setOpacity(this.transparencyOldSceneOpacity.value)

    DEBUG.get().start('anim_time')
    this.currentModel?.animationMixers.forEach(animation => animation.update(delta))
    DEBUG.get().end('anim_time')

    DEBUG.get().start('label_time')
    this.labelsManager?.render(this.scene, this.currentCamera)
    DEBUG.get().end('label_time')

    DEBUG.get().start('drill_label_time')
    this.drillDataSetManager?.render(this.scene, this.currentCamera)
    DEBUG.get().end('drill_label_time')

    this.targetVisualizer?.render(this.scene, this.currentCamera)

    DEBUG.get().start('render_time')
    this._effectComposer.render()
    DEBUG.get().end('render_time')

    this.needsRender = !!this.currentModel?.animationMixers.length
    DEBUG.get().end('r')
    this.debugRender()
    DEBUG.get().update()
    this.renderer.info.reset()
  }

  setLayerVisibility(layers: NodeVisibility[], { debounced = false } = {}) {
    layers.forEach(l => {
      const sceneNode = this.currentModel?.sceneNodesById.get(l.nodeId)

      if (!sceneNode) return

      sceneNode.visible = l.visible

      if (!sceneNode.visible) disposeScene(sceneNode)

      if (sceneNode.userData.isVisibleForLayerHighlighting) {
        sceneNode.userData.wasVisible = l.visible
      }

      this.changedLayers[l.nodeId] = l.visible
    })

    if (debounced) {
      this.debouncedOnLayerVisibilityChange()
      return
    }

    this.needsRender = true
    this.onLayerVisibilityChange()
  }

  debouncedOnLayerVisibilityChange = debounce(() => {
    this.needsRender = true
    this.onLayerVisibilityChange()
  }, 500)

  startLayerTransition(nodes: NodeVisibility[]) {
    this.transparencyPass.enabled = true
    this.transparencyOldScene.clear()
    this.transparencyOldScene.copy(this.scene)
    this.setLayerVisibility(nodes)

    this.transparencyTween?.kill()
    this.transparencyTween = gsap.fromTo(
      this.transparencyOldSceneOpacity,
      { value: 1 },
      {
        value: 0,
        duration: DEFAULT_MODEL_TWEEN_DURATION,
        onUpdate: () => {
          this.needsRender = true
        },
        onComplete: () => {
          // TODO dispose whats necessary not everything
          this.cleanUpOldSceneTransition()
          this.needsRender = true
        },
        // TODO: We can do something with this to make interrupts smoother (potentially)
        onInterrupt: () => {
          this.drillDataSetManager?.drillLabelManager.disposeTransitionDrillLabels()
        },
      },
    )
  }

  async getThumbnailBlob({ width, height } = { width: 450, height: 225 }): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      if (!this.labelsManager) {
        reject('No labelsManager')
        return
      }

      // We need to match the aspect ratio of the thumbnail
      const originalAspectRatio = this.currentCamera.aspect

      this.currentCamera.aspect = width / height
      this.currentCamera.updateProjectionMatrix()

      const offscreenRenderTarget = new WebGLRenderTarget(width, height, {
        samples: 8,
      })

      this.renderer.setRenderTarget(offscreenRenderTarget)
      this.labelsManager.applyThumbnailMaterial()
      await this.drillDataSetManager?.drillLabelManager.applyThumbnailMaterial()
      // toggling autoclear to control stacking backdrop and foreground
      this.renderer.autoClear = false
      this.backdropQuad.render(this.renderer)
      this.renderer.clearDepth()
      this.renderer.render(this.scene, this.currentCamera)
      this.renderer.autoClear = true
      this.labelsManager.applyCutoutMaterial()
      this.drillDataSetManager?.drillLabelManager.applyCutoutMaterial()

      const pixels = new Uint8Array(width * height * 4)
      this.renderer.readRenderTargetPixels(offscreenRenderTarget, 0, 0, width, height, pixels)

      const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height)

      const offscreenCanvas = document.createElement('canvas') as HTMLCanvasElement
      offscreenCanvas.width = width
      offscreenCanvas.height = height

      offscreenCanvas.getContext('2d')?.putImageData(this._mirrorImageVertically(imageData), 0, 0)

      // Reset the camera aspect ratio
      this.currentCamera.aspect = originalAspectRatio
      this.currentCamera.updateProjectionMatrix()

      offscreenCanvas.toBlob(
        blob => {
          if (!blob) {
            reject('Failed to save canvas. Blob was null.')
            return
          }

          this.renderer.setRenderTarget(null)
          offscreenRenderTarget.dispose()
          resolve(blob)
        },
        'image/jpeg',
        0.8,
      )
    })
  }

  private cleanUpOldSceneTransition() {
    disposeScene(this.transparencyOldScene)
    this.drillDataSetManager?.drillLabelManager.disposeTransitionDrillLabels()
    this.transparencyPass.enabled = false
    DEBUG.get().set('transparency_time', 0)
  }

  private _mirrorImageVertically(imageData: ImageData): ImageData {
    const width = imageData.width
    const height = imageData.height
    const mirroredData = new ImageData(width, height)

    for (let i = 0; i < imageData.data.length; i += 4) {
      let row = Math.floor(i / (width * 4))
      let col = i % (width * 4)

      let mirroredRow = height - 1 - row

      let mirroredIndex = mirroredRow * (width * 4) + col

      mirroredData.data[mirroredIndex] = imageData.data[i] // R
      mirroredData.data[mirroredIndex + 1] = imageData.data[i + 1] // G
      mirroredData.data[mirroredIndex + 2] = imageData.data[i + 2] // B
      mirroredData.data[mirroredIndex + 3] = imageData.data[i + 3] // A
    }

    return mirroredData
  }
}

// Given a camera and an object size, find the minimum distance to view the entire object.
function _getObjectFitDistance(camera: PerspectiveCamera, maxSize: number): number {
  const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360))
  const fitWidthDistance = fitHeightDistance / camera.aspect

  return Math.max(fitHeightDistance, fitWidthDistance)
}
